import { MAX_LEVELS } from '../config/GameConfig';

export interface LevelRecord {
  stars: number;
  bestTime: number;
  bestScore: number;
}

export interface SaveData {
  version: 1;
  playerId: string;
  playerName: string;
  unlockedLevel: number;
  completedLevels: Record<number, LevelRecord>;
  settings: {
    musicVolume: number;
    effectsVolume: number;
    muted: boolean;
    quality: 'LOW' | 'MEDIUM' | 'HIGH';
  };
}

const STORAGE_KEY = 'mouthbound-ruins-save-v1';

export function defaultSave(quality: SaveData['settings']['quality'] = 'MEDIUM'): SaveData {
  return {
    version: 1,
    playerId: createPlayerId(),
    playerName: 'Adventurer',
    unlockedLevel: 1,
    completedLevels: {},
    settings: { musicVolume: 0.42, effectsVolume: 0.72, muted: false, quality },
  };
}

export function sanitizeSave(value: unknown, quality: SaveData['settings']['quality'] = 'MEDIUM'): SaveData {
  const fallback = defaultSave(quality);
  if (!value || typeof value !== 'object') return fallback;
  const candidate = value as Partial<SaveData>;
  if (candidate.version !== 1) return fallback;
  const settings = candidate.settings ?? fallback.settings;
  return {
    version: 1,
    playerId: sanitizePlayerId(candidate.playerId) || fallback.playerId,
    playerName: sanitizePlayerName(candidate.playerName),
    unlockedLevel: Math.max(1, Math.min(MAX_LEVELS, Math.floor(candidate.unlockedLevel ?? 1))),
    completedLevels: candidate.completedLevels && typeof candidate.completedLevels === 'object'
      ? candidate.completedLevels : {},
    settings: {
      musicVolume: clamp(settings.musicVolume ?? fallback.settings.musicVolume),
      effectsVolume: clamp(settings.effectsVolume ?? fallback.settings.effectsVolume),
      muted: Boolean(settings.muted),
      quality: ['LOW', 'MEDIUM', 'HIGH'].includes(settings.quality) ? settings.quality : quality,
    },
  };
}

function clamp(value: number): number { return Math.max(0, Math.min(1, Number(value) || 0)); }

export function sanitizePlayerName(value: unknown): string {
  const clean = String(value ?? '').normalize('NFKC').replace(/[^\p{L}\p{N} ._'-]/gu, '').replace(/\s+/g, ' ').trim().slice(0, 18);
  return clean || 'Adventurer';
}

function sanitizePlayerId(value: unknown): string {
  const id = String(value ?? '');
  return /^[a-zA-Z0-9-]{16,64}$/.test(id) ? id : '';
}

function createPlayerId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `player-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export class SaveSystem {
  data: SaveData;

  constructor(initialQuality: SaveData['settings']['quality']) {
    this.data = this.load(initialQuality);
    this.save();
  }

  setPlayerName(name: unknown): string {
    this.data.playerName = sanitizePlayerName(name);
    this.save();
    return this.data.playerName;
  }

  completeLevel(level: number, record: LevelRecord): void {
    const previous = this.data.completedLevels[level];
    this.data.completedLevels[level] = {
      stars: Math.max(previous?.stars ?? 0, record.stars),
      bestTime: previous ? Math.min(previous.bestTime, record.bestTime) : record.bestTime,
      bestScore: Math.max(previous?.bestScore ?? 0, record.bestScore),
    };
    this.data.unlockedLevel = Math.min(MAX_LEVELS, Math.max(this.data.unlockedLevel, level + 1));
    this.save();
  }

  save(): void {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data)); } catch { /* private mode */ }
  }

  private load(quality: SaveData['settings']['quality']): SaveData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? sanitizeSave(JSON.parse(raw), quality) : defaultSave(quality);
    } catch { return defaultSave(quality); }
  }
}
